import { defineCopy } from '@/copy';

/**
 * stats strings. Voice: short, funny, rib the player and never the person. No em dashes.
 * Badge names and blurbs live with the badge definitions in shared/src/domain/stats/badges.ts.
 */
export const copy = defineCopy('stats', {
  title: 'Stats',
  nav: {
    label: 'Stats sections',
    leaders: 'Leaders',
    records: 'Records',
    h2h: 'Head to head',
    feed: 'Feed',
  },
  leaders: {
    title: 'Leaderboard',
    empty: 'Play a game and the board fills itself.',
    emptyBody: 'Numbers show up after the first finished game.',
    windowLabel: 'Time window',
    windowAll: 'All time',
    window30: '30 days',
    statLabel: 'Pick a stat',
    games: '{n} games',
    game: '1 game',
    you: 'You',
    yourRow: 'Your spot',
    top: 'Top',
    rank: 'Rank {n}',
    loadError: 'The board did not load. Pull down and try again.',
    openPlayer: 'Open {name}',
  },
  stat: {
    ovr: 'OVR',
    ppg: 'PPG',
    tpm: '3PM',
    fg_pct: 'FG%',
    tp_pct: '3P%',
    ft_pct: 'FT%',
    wins: 'Wins',
    win_pct: 'Win%',
    streak: 'Streak',
    rpg: 'RPG',
    apg: 'APG',
    spg: 'SPG',
    bpg: 'BPG',
    elo: 'Elo',
    gp: 'Games',
    attr_3PT: 'Three',
    attr_MID: 'Mid',
    attr_FIN: 'Finish',
    attr_FT: 'Free throw',
    attr_PLAYMAKING: 'Playmaking',
    attr_DEF: 'Defense',
    attr_REB: 'Rebounding',
    attr_HUSTLE: 'Hustle',
    attr_CLU: 'Clutch',
  },
  statHint: {
    ovr: 'Overall rating, 0 to 99.',
    ppg: 'Points per game, everything counted in 2s and 3s.',
    tpm: 'Threes made per game.',
    fg_pct: 'Field goal percentage.',
    tp_pct: 'Three-point percentage.',
    ft_pct: 'Free throw percentage.',
    wins: 'Games won.',
    win_pct: 'Share of games won.',
    streak: 'Longest run of wins.',
    rpg: 'Rebounds per game.',
    apg: 'Assists per game.',
    spg: 'Steals per game.',
    bpg: 'Blocks per game.',
    elo: 'Elo, 1v1 and team games blended.',
    gp: 'Games played.',
    attr: 'Attribute rating, 0 to 99.',
  },
  records: {
    title: 'Crew records',
    empty: 'No records yet. Easy to break.',
    emptyBody: 'Hit a big number and your name goes up here.',
    heldBy: 'Held by',
    tied: 'Tied',
    gameShort: 'Game',
    openGame: 'Open game',
    loadError: 'The record book did not load.',
    noGame: 'From the combine',
  },
  unit: {
    pts_game: 'points',
    tpm_game: 'threes',
    reb_game: 'boards',
    ast_game: 'dimes',
    stl_game: 'steals',
    blk_game: 'blocks',
    gmsc21_game: 'game score',
    win_margin: '% of the target',
    comeback: 'points down',
    win_streak: 'wins in a row',
    three_round_pct: '% from three',
    drill_streak: 'makes in a row',
  },
  h2h: {
    title: 'Head to head',
    matrixTitle: 'The 1v1 grid',
    matrixHint: 'Row against column. Tap a cell to open the matchup.',
    matrixEmpty: 'No 1v1 games yet. The grid is waiting.',
    pickA: 'Player one',
    pickB: 'Player two',
    pickPlaceholder: 'Pick a player',
    swap: 'Swap',
    empty: 'These two have never met. Fix that.',
    startOneVone: 'Start a 1v1',
    oneVone: '1v1 record',
    teammates: 'As teammates',
    opposed: 'Against each other in team games',
    rivalry: 'Rivalry',
    pointsFor: 'Points per game',
    last: 'Last games',
    rivalries: 'Hottest rivalries',
    rivalriesEmpty: 'No rivalries yet. It takes 4 close 1v1 games.',
    nemesis: 'Your nemesis: {name} ({w}-{l}). Maybe try passing.',
    customer: 'Favorite customer: {name} ({w}-{l}). Send flowers.',
    sameTwice: 'Pick two different people.',
    record: '{w}-{l}',
    games: '{n} games',
    loadError: 'Head to head did not load.',
  },
  player: {
    title: 'Player stats',
    back: 'Back to the board',
    rating: 'Rating',
    history: 'Rating history',
    historyEmpty: 'Not enough games for a line yet.',
    historyOnePoint: 'One point is a dot, not a trend. Play more.',
    metric: 'Chart',
    attributes: 'Attributes',
    attributesNote: 'Grey ?? means the app has not seen enough of it yet.',
    stats: 'Numbers',
    badges: 'Badges',
    badgesLocked: 'Locked',
    badgeTier: 'Level {n}',
    log: 'Game log',
    logEmpty: 'No finished games yet.',
    rivals: 'Rivals',
    fullStats: 'Full stats',
    profileEmpty: 'Nothing on the board yet.',
    won: 'W',
    lost: 'L',
    tied: 'T',
    eloLine: '1v1 {a} · team {b}',
    elo1: '1v1 Elo',
    elo: 'Elo',
    gamesPlayed: 'Games',
    record: 'Record',
    notTracked: 'n/a',
    sample: '{n} of {of} games tracked',
    streak: 'Streak',
    bestStreak: 'Best streak {n}',
    openGame: 'Open game',
    loadError: 'This player did not load.',
    unknown: 'No such player on the board.',
  },
  stats: {
    ppg: 'PPG',
    rpg: 'RPG',
    apg: 'APG',
    spg: 'SPG',
    bpg: 'BPG',
    tov: 'TOV',
    pf: 'PF',
    tpm: '3PM',
    gmsc: 'GmSc',
    plus_minus: '+/-',
    fg_pct: 'FG%',
    tp_pct: '3P%',
    ft_pct: 'FT%',
    efg_pct: 'eFG%',
    ts_pct: 'TS%',
  },
  state: {
    unrated: "Card's in the oven. Finish the combine or play 3 games.",
    provisional: "{n} more games and it's official.",
    provisionalDone: 'Capped at 79 until you prove it in games.',
    rusty: 'Last bucket: {n} days ago.',
    rustyToday: 'Last bucket: today.',
    tagProvisional: 'Provisional',
    tagRusty: 'Rusty',
    tagUnrated: 'Combine pending',
  },
  delta: {
    ovrUp: 'OVR {n}. Your knees filed a complaint, but the number went up.',
    ovrDown: 'OVR {n}. The knees were right.',
    ovrSame: 'OVR {n}. Holding steady, like a statue.',
    unrated: "??: Card's in the oven. Finish the combine or play 3 games.",
    promoted: "Promoted to {tier}. Act like you've been here.",
    demoted: 'Back to {tier}. Happens to the best. Mostly to the rest.',
    up: {
      '3PT': '+{n} 3PT. The jumper is getting wet.',
      MID: '+{n} MID. The elbow is open for business.',
      FIN: '+{n} FIN. Layups are layups again.',
      FT: '+{n} FT. The line likes you.',
      PLAYMAKING: '+{n} PLY. Dimes on dimes.',
      DEF: '+{n} DEF. Somebody got cooked, and it was not you.',
      REB: '+{n} REB. Boxing out like rent is due.',
      HUSTLE: '+{n} HUS. Dove on the floor, again.',
      CLU: '+{n} CLU. Ice in the veins.',
    },
    down: {
      '3PT': '-{n} 3PT. The rim sent a cease and desist.',
      MID: '-{n} MID. The elbow filed for bankruptcy.',
      FIN: '-{n} FIN. The rim said no, politely.',
      FT: '-{n} FT. The line is not your friend.',
      PLAYMAKING: '-{n} PLY. The pass found the third row.',
      DEF: '-{n} DEF. Somebody got cooked.',
      REB: '-{n} REB. The ball went elsewhere.',
      HUSTLE: '-{n} HUS. The floor stayed clean.',
      CLU: '-{n} CLU. Cold hands, warm excuses.',
    },
  },
  feed: {
    title: 'Latest results',
    page: 'What went down',
    line: 'No results yet. The bricks are off the record.',
    empty: 'Nothing yet. Somebody go miss a layup.',
    seeAll: 'See all',
    more: 'Load more',
    end: 'That is everything. Go play something.',
    loadError: 'The feed did not load.',
    justNow: 'just now',
    minutes: '{n}m ago',
    hours: '{n}h ago',
    yesterday: 'yesterday',
    days: '{n}d ago',
    kind: {
      game_final: 'Final',
      record_broken: 'Record',
      badge: 'Badge',
      milestone: 'Milestone',
      tier_change: 'Tier',
      portrait_ready: 'New card',
      lab_result: 'Lab',
      session_recap: 'Recap',
      member_joined: 'New',
      tournament_final: 'Bracket',
      combine_done: 'Combine',
    },
  },
  chart: {
    label: 'Rating over time for {name}',
    ovr: 'OVR',
    from: 'From {date}',
    to: 'To {date}',
    now: 'Now {n}',
  },
});
